import React, { useState } from "react";
import { getLocale } from "egov-ui-kit/utils/localStorageUtils";
import { formatINR, sumField, safeArray } from "egov-ui-kit/utils/commons";

const LEVELS = [
  { key: "ulbs", label: "ULB Name", childKey: "zones", type: "static" },
  { key: "zones", label: "Zone Name", childKey: "departments", type: "static" },
  { key: "departments", label: "Department Name", showCount: false, childKey: "fund", type: "computed", groupField: "department" },
  { key: "fund", label: "Fund", showCount: false, childKey: "budgetAccountHeads", type: "api" },
  { key: "budgetAccountHeads", label: "Account Name", childKey: "purchaseOrders", type: "nested" },
  { key: "purchaseOrders", label: "Purchase Order", childKey: null, type: "leaf" },
];

const formatDate = (d) => (d ? String(d).split(" ")[0] : "-");
const groupDetails = (details, groupField) => {
  const map = new Map();
  safeArray(details).forEach((r) => {
    const key = r[groupField];
    if (!map.has(key)) {
      map.set(key, { id: key, name: key, departmentDetails: [] });
    }
    map.get(key).departmentDetails.push(r);
  });
  return Array.from(map.values()).map((entry) => ({
    ...entry,
    totalPurchaseOrder: sumField(entry.departmentDetails, "totalOrderValue"),
    totalNoOfPurchaseOrder: entry.departmentDetails.length,
  }));
};

const normalizeBudgetHeads = (heads) =>
  safeArray(heads).map((h, idx) => ({
    id: h.id || `head_${idx}`,
    name: h.budgetAccountHead,
    glCode: h.glCode,
    totalPurchaseOrder: h.orderValue,
    totalNoOfPurchaseOrder: h.orderCount || safeArray(h.purchaseOrders).length,
    purchaseOrders: safeArray(h.purchaseOrders),
  }));

const normalizeFundList = (rawResponse) => {
  const list = safeArray(rawResponse && rawResponse.fundDetails);
  return list.map((f, idx) => {
    const budgetAccountHeads = normalizeBudgetHeads(f.budgetAccountHeads);
    const totalNoOfPurchaseOrder = sumField(budgetAccountHeads, "totalNoOfPurchaseOrder");
    return {
      id: f.id || `fund_${idx}`,
      name: f.fund,
      totalPurchaseOrder: f.totalOrderValue,
      totalNoOfPurchaseOrder,
      budgetAccountHeads,
    };
  });
};

const PurchaseOrderTable = ({ ulbType, fromDate, toDate, purchaseOrderDetails, purchaseOrderFundDetails, fetchPurchaseOrderFundDetails }) => {
  const tableData = purchaseOrderDetails && purchaseOrderDetails;
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);

  const levelIndex = breadcrumb.length;
  const levelConfig = LEVELS[levelIndex];
  const canDrillDown = levelConfig.childKey !== null;
  const isRootLevel = levelIndex === 0;
  const isDepartmentLevel = levelConfig.key === "departments";
  const isFundLevel = levelConfig.key === "fund";
  const isLeafLevel = levelConfig.key === "purchaseOrders";
  const isBudgetHeadLevel = levelConfig.key === "budgetAccountHeads";
  const showCount = levelConfig.showCount !== false; // default true unless explicitly false

  const currentRows = (function () {
    if (isRootLevel) {
      return safeArray(tableData[LEVELS[0].key]);
    }
    var currentKey = levelConfig.key;
    var parentItem = breadcrumb[levelIndex - 1].item;

    if (currentKey === "fund") {
      return normalizeFundList(purchaseOrderFundDetails);
    }
    if (currentKey === "budgetAccountHeads" || currentKey === "purchaseOrders") {
      return safeArray(parentItem[currentKey]);
    }
    if (levelConfig.type === "static") {
      return safeArray(parentItem[levelConfig.key]);
    }
    return groupDetails(parentItem.departmentDetails, levelConfig.groupField);
  })();

  const currentTitle = isRootLevel ? "ULB Wise Purchase Order" : breadcrumb[levelIndex - 1].item.name;
  const totalsRow = {
    totalPurchaseOrder: sumField(currentRows, "totalPurchaseOrder"),
    totalNoOfPurchaseOrder: sumField(currentRows, "totalNoOfPurchaseOrder"),
  };

  // Colspan for the "empty" row needs to shrink when the count column is hidden
  const emptyColSpan = 1 + (isBudgetHeadLevel ? 1 : 0) + (showCount ? 1 : 0) + 1;

  // —— Handlers ———————————————————————————————
  const handleDrillDown = (item) => {
    if (!canDrillDown) return;

    if (levelConfig.key === "zones") {
      setSelectedZone(item);
    }

    const departmentCode = item.departmentDetails ? item.departmentDetails[0].departmentCode : null;

    if (isDepartmentLevel) {
      const payload = {
        financialYear: tableData.financialYear,
        ulbName: breadcrumb[0] ? breadcrumb[0].item.tenantId : null,
        zoneName: selectedZone ? selectedZone.name : null,
        departmentCode: departmentCode,
        ulbType: ulbType,
        fromDate: fromDate,
        toDate: toDate,
        locale: getLocale(),
      };
      fetchPurchaseOrderFundDetails(payload);
    }

    setBreadcrumb(function (prev) {
      return prev.concat([{ levelIndex: levelIndex, item: item }]);
    });
  };

  const handleBack = () => {
    setBreadcrumb(function (prev) {
      var newBreadcrumb = prev.slice(0, -1);
      if (newBreadcrumb.length < 2) setSelectedZone(null);
      return newBreadcrumb;
    });
  };

  const handleBreadcrumbClick = (toIndex) => {
    setBreadcrumb(function (prev) {
      var newBreadcrumb = prev.slice(0, toIndex);
      if (newBreadcrumb.length < 2) setSelectedZone(null);
      return newBreadcrumb;
    });
  };

  const emptyMessage = isFundLevel ? "Loading fund details..." : "No Record Found";

  // —— Render —————————————————────────────────
  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          {isRootLevel ? (
            <div className="icon-wrapper">
              <i className="material-icons">description</i>
            </div>
          ) : (
            <button className="btn btn-secondary back-button" onClick={handleBack}>
              Back
            </button>
          )}
          <div>
            <h2>{currentTitle}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {tableData.financialYear}</span>
          <span>|</span>
          <span>ULB : {tableData.ulbName}</span>
        </div>
      </div>

      {/* BREADCRUMB */}
      {!isRootLevel && (
        <div className="breadcrumb-bar">
          <span className="bc-link" onClick={() => handleBreadcrumbClick(0)} role="button" tabIndex={0}>
            ULB Wise Purchase Order
          </span>
          {breadcrumb.map(function (bc, idx) {
            var isLast = idx === breadcrumb.length - 1;
            return (
              <React.Fragment key={bc.item.id}>
                <span className="bc-sep"> › </span>
                {isLast ? (
                  <span className="bc-current">{bc.item.name}</span>
                ) : (
                  <span className="bc-link" onClick={() => handleBreadcrumbClick(idx + 1)} role="button" tabIndex={0}>
                    {bc.item.name}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* MAIN TABLE */}
      <div
        className={`table-responsive ${levelConfig.key === "ulbs" || levelConfig.key === "zones" || levelConfig.key === "departments" ? "scrolling-table" : ""}`}
      >
        {isLeafLevel ? (
          // ── LEAF LEVEL: individual Purchase Orders ──
          <table className="table receipt-table mb-0">
            <thead>
              <tr>
                <th>Purchase Order No.</th>
                <th>Purchase Order Date</th>
                <th>Supplier Name</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? (
                currentRows.map(function (po, idx) {
                  return (
                    <tr key={po.id || idx}>
                      <td>{po.proposalNo}</td>
                      <td>{formatDate(po.poDate)}</td>
                      <td>{po.supplierName}</td>
                      <td>
                        <span className={`status-badge status-${String(po.status || "").toLowerCase()}`}>{po.status}</span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          // ── ULB / ZONE / DEPARTMENT / FUND / BUDGET-HEAD LEVELS ──
          <table className="table receipt-table mb-0">
            <thead>
              <tr>
                <th>{levelConfig.label}</th>
                {isBudgetHeadLevel && <th>Account Code</th>}
                {showCount && <th>Total No. of Purchase Orders</th>}
                <th>Total Purchase Order Value</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? (
                <React.Fragment>
                  {currentRows.map(function (item) {
                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        {isBudgetHeadLevel && <td>{item.glCode}</td>}
                        {showCount && (
                          <td>
                            {canDrillDown ? (
                              
                              <a  href="#"
                                className="link-cell"
                                onClick={function (e) {
                                  e.preventDefault();
                                  handleDrillDown(item);
                                }}
                              >
                                {formatINR(item.totalNoOfPurchaseOrder)}
                              </a>
                            ) : (
                              formatINR(item.totalNoOfPurchaseOrder)
                            )}
                          </td>
                        )}
                        <td>
                          {canDrillDown ? (
                            
                             <a href="#"
                              className="link-cell"
                              onClick={function (e) {
                                e.preventDefault();
                                handleDrillDown(item);
                              }}
                            >
                              {formatINR(item.totalPurchaseOrder)}
                            </a>
                          ) : (
                            formatINR(item.totalPurchaseOrder)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ) : (
                <tr>
                  <td colSpan={emptyColSpan} style={{ textAlign: "center" }}>
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>

            <tr className="total-row"></tr>

            {currentRows.length > 0 && (
              <tfoot className="tfoot-bottom">
                <tr className="total-row">
                  <td style={{ fontWeight: "bold", textAlign: "left" }}>Total</td>
                  {isBudgetHeadLevel && <td></td>}
                  {showCount && <td style={{ fontWeight: "bold" }}>{formatINR(totalsRow.totalNoOfPurchaseOrder)}</td>}
                  <td style={{ fontWeight: "bold" }}>{formatINR(totalsRow.totalPurchaseOrder)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrderTable;


// WEBPACK FOOTER //
// src/modules/employee/Home/PurchaseOrderTable.js